import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChevronRight, Home, ChevronLeft } from 'lucide-react';

interface BreadcrumbItem {
  name: string;
  path: string;
  isActive: boolean;
}

export default function Breadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [history, setHistory] = useState<BreadcrumbItem[]>([]);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // Récupérer l'historique depuis sessionStorage
    const storedHistory = sessionStorage.getItem('breadcrumbHistory');
    const currentPath = location.pathname;
    
    // Fonction pour générer un nom lisible à partir du chemin
    const getReadableName = (path: string): string => {
      const segments = path.split('/').filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      
      // Si c'est un ID numérique, essayer de trouver un nom plus parlant
      if (!isNaN(Number(lastSegment))) {
        const previousSegment = segments[segments.length - 2] || '';
        // Essayer de deviner le nom de la ressource
        const resourceNames: { [key: string]: string } = {
          'products': 'Produit',
          'market': 'Marché',
          'analyse': 'Analyse',
          'consultation': 'Consultation',
          'profile': 'Profil',
          'settings': 'Paramètres',
          // Ajouter d'autres mappings ici
        };
        const resourceName = resourceNames[previousSegment] || 'Détail';
        return `${resourceName} ${lastSegment}`;
      }
      
      // Remplacer les tirets par des espaces et mettre en majuscule
      return lastSegment
        .replace(/-/g, ' ')
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // Construire l'historique complet
    const buildHistory = () => {
      const historyItems: BreadcrumbItem[] = [];
      
      // Ajouter l'accueil
      historyItems.push({
        name: t('home'),
        path: '/',
        isActive: currentPath === '/'
      });

      // Si on est sur la page d'accueil, ne pas ajouter plus de chemins
      if (currentPath === '/') {
        return historyItems;
      }

      // Construire les chemins intermédiaires
      const segments = currentPath.split('/').filter(Boolean);
      let accumulatedPath = '';
      
      segments.forEach((segment, index) => {
        accumulatedPath += '/' + segment;
        const isLast = index === segments.length - 1;
        
        // Ignorer les segments qui sont des IDs numériques pour ne pas les afficher seuls
        if (!isNaN(Number(segment)) && index > 0) {
          // Pour les IDs, on utilise le nom de la page précédente
          const prevSegment = segments[index - 1] || 'Détail';
          const resourceName = getReadableName(prevSegment);
          historyItems.push({
            name: `${resourceName} ${segment}`,
            path: accumulatedPath,
            isActive: isLast
          });
        } else {
          historyItems.push({
            name: t(segment) || getReadableName(segment),
            path: accumulatedPath,
            isActive: isLast
          });
        }
      });

      return historyItems;
    };

    // Si un historique est stocké, l'utiliser, sinon en créer un
    if (storedHistory) {
      try {
        const parsedHistory = JSON.parse(storedHistory);
        // Vérifier si l'historique stocké correspond au chemin actuel
        const lastItem = parsedHistory[parsedHistory.length - 1];
        if (lastItem && lastItem.path === currentPath) {
          setHistory(parsedHistory);
          return;
        }
      } catch (e) {
        // Ignorer les erreurs de parsing
      }
    }

    // Créer un nouvel historique
    const newHistory = buildHistory();
    setHistory(newHistory);
    
    // Stocker dans sessionStorage
    sessionStorage.setItem('breadcrumbHistory', JSON.stringify(newHistory));

    // Nettoyer les histoires obsolètes
    const cleanupHistory = () => {
      const allHistories = Object.keys(sessionStorage)
        .filter(key => key.startsWith('breadcrumbHistory_'));
      
      // Garder seulement les 10 dernières histoires
      if (allHistories.length > 10) {
        const sorted = allHistories.sort();
        const toRemove = sorted.slice(0, allHistories.length - 10);
        toRemove.forEach(key => sessionStorage.removeItem(key));
      }
    };
    cleanupHistory();

  }, [location.pathname, t]);

  // Fonction pour revenir en arrière
  const goBack = () => {
    navigate(-1);
  };

  // Fonction pour obtenir le chemin affichable
  const getDisplayName = (item: BreadcrumbItem): string => {
    if (item.path === '/') return t('home');
    
    // Si le nom contient des caractères spéciaux, les traiter
    return item.name
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <nav 
      className="flex items-center gap-2 text-sm"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Bouton retour */}
      <button
        onClick={goBack}
        className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 group"
        aria-label="Retour"
      >
        <ChevronLeft 
          size={16} 
          className="group-hover:-translate-x-0.5 transition-transform" 
        />
        <span className="text-xs font-medium hidden sm:inline">
          {t('retour') || 'Retour'}
        </span>
      </button>

      {/* Séparateur */}
      <span className="text-gray-300 select-none">|</span>

      {/* Fil d'Ariane */}
      <ul className="flex items-center gap-1.5 flex-wrap">
        {history.map((item, idx) => {
          const isLast = idx === history.length - 1;
          const isFirst = idx === 0;

          return (
            <li key={item.path} className="flex items-center gap-1.5">
              {/* Icône maison pour l'accueil */}
              {isFirst && (
                <Link
                  to={item.path}
                  className="flex items-center gap-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                >
                  <Home size={14} />
                </Link>
              )}

              {/* Séparateur entre les éléments */}
              {!isFirst && (
                <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
              )}

              {/* Lien du fil d'Ariane */}
              {!isLast ? (
                <Link
                  to={item.path}
                  className={`
                    px-2 py-0.5 rounded-md text-gray-500 
                    hover:text-green-600 hover:bg-green-50 
                    transition-all duration-200
                    ${isFirst ? 'text-gray-400' : ''}
                  `}
                >
                  {isFirst ? '' : getDisplayName(item)}
                </Link>
              ) : (
                <span 
                  className={`
                    px-2 py-0.5 rounded-md font-semibold
                    ${isFirst ? 'text-gray-400' : 'text-gray-800'}
                  `}
                >
                  {isFirst ? t('home') : getDisplayName(item)}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/* Indicateur de position */}
      {history.length > 1 && (
        <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full font-medium">
          {history.length - 1} {t('pages') || 'pages'}
        </span>
      )}
    </nav>
  );
}